/* eslint-disable react/function-component-definition */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { getCookies } from "../../hooks/useCookies";
import { REACT_APP_API_CAFE_TRANSACTION_REPORT } from "../../redux/apiConstants";
import styles from "./CafeTransactionReportModal.module.scss";

const REPORT_TYPES = [
  {
    key: "Detailed",
    label: "Detailed",
    icon: "fa-list",
    description: "Line items, tips, CC fees, and payouts per order",
  },
  {
    key: "Summary",
    label: "Summary",
    icon: "fa-table",
    description: "Daily sales, tips, refunds, fees, and total payment",
  },
];

const TIME_FRAMES = [
  { key: "24 Hours", label: "24 Hours" },
  { key: "Last 7 Days", label: "Last 7 Days" },
  { key: "Last 30 Days", label: "Last 30 Days" },
  { key: "Custom", label: "Custom" },
];

const parseExportError = async (error) => {
  const data = error.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return json.message || "Export failed. Please try again.";
    } catch (err) {
      return "Export failed. Please try again.";
    }
  }
  return error.response?.data?.message || "Export failed. Please try again.";
};

const CafeTransactionReportModal = ({
  show,
  handleClose,
  cafeId,
  cafeName,
}) => {
  const [reportType, setReportType] = useState("Detailed");
  const [timeFrame, setTimeFrame] = useState("Last 7 Days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const token = getCookies("Token");

  const resetState = () => {
    setReportType("Detailed");
    setTimeFrame("Last 7 Days");
    setStartDate("");
    setEndDate("");
    setLoading(false);
  };

  const handleCloseModal = () => {
    if (loading) return;
    resetState();
    handleClose();
  };

  const handleExport = async () => {
    if (!cafeId) {
      toast.error("Cafe is required.");
      return;
    }
    if (timeFrame === "Custom" && (!startDate || !endDate)) {
      toast.error("Please choose a start and end date.");
      return;
    }
    if (timeFrame === "Custom" && startDate > endDate) {
      toast.error("Start date must be on or before end date.");
      return;
    }

    setLoading(true);
    try {
      const params = { reportType, timeFrame };
      if (timeFrame === "Custom") {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const response = await axios.get(
        REACT_APP_API_CAFE_TRANSACTION_REPORT(cafeId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
          responseType: "blob",
          timeout: 120000,
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = `${cafeName || "Cafe"} ${reportType} Report.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded.");
      resetState();
      handleClose();
    } catch (error) {
      console.error("Cafe transaction report error:", error);
      toast.error(await parseExportError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleCloseModal}
      centered
      className={styles.exportModal}
    >
      <div className={styles.modalHeader}>
        <h5 className={styles.modalTitle}>
          <i className="fa fa-file-excel" />
          Export Transaction Report
        </h5>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleCloseModal}
          aria-label="Close modal"
          disabled={loading}
        >
          <i className="fa fa-times" />
        </button>
      </div>

      <Modal.Body className={styles.modalBody}>
        {loading ? (
          <div className={styles.loadingBox}>
            <Spinner animation="border" className={styles.spinner} />
            <h5>Preparing report...</h5>
            <p>This uses the same Excel format cafes receive in Reports.</p>
          </div>
        ) : (
          <>
            {cafeName ? (
              <p className={styles.cafeName}>
                Cafe: <strong>{cafeName}</strong>
              </p>
            ) : null}

            <div className={styles.formatSection}>
              <h6 className={styles.sectionTitle}>
                <i className="fa fa-file-alt" />
                Report type
              </h6>
              <div className={styles.formatOptions}>
                {REPORT_TYPES.map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    className={`${styles.formatBtn} ${
                      reportType === type.key ? styles.active : ""
                    }`}
                    onClick={() => setReportType(type.key)}
                    aria-label={`Export ${type.label} report`}
                  >
                    <i className={`fa ${type.icon}`} />
                    <span className={styles.formatLabel}>{type.label}</span>
                    <span className={styles.formatDesc}>{type.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.formatSection}>
              <h6 className={styles.sectionTitle}>
                <i className="fa fa-calendar" />
                Time frame
              </h6>
              <div className={styles.timeFrameOptions}>
                {TIME_FRAMES.map((frame) => (
                  <button
                    key={frame.key}
                    type="button"
                    className={`${styles.timeFrameBtn} ${
                      timeFrame === frame.key ? styles.active : ""
                    }`}
                    onClick={() => setTimeFrame(frame.key)}
                    aria-label={`Time frame ${frame.label}`}
                  >
                    {frame.label}
                  </button>
                ))}
              </div>
            </div>

            {timeFrame === "Custom" && (
              <div className={styles.dateRow}>
                <Form.Group className={styles.dateField}>
                  <Form.Label>Start date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label="Report start date"
                  />
                </Form.Group>
                <Form.Group className={styles.dateField}>
                  <Form.Label>End date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    aria-label="Report end date"
                  />
                </Form.Group>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <div className={styles.modalFooter}>
        <Button
          variant="secondary"
          onClick={handleCloseModal}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          className={styles.primaryBtn}
          onClick={handleExport}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" />
              Exporting...
            </>
          ) : (
            <>
              <i className="fa fa-download" />
              Download Excel
            </>
          )}
        </Button>
      </div>
    </Modal>
  );
};

CafeTransactionReportModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  cafeId: PropTypes.string,
  cafeName: PropTypes.string,
};

CafeTransactionReportModal.defaultProps = {
  cafeId: "",
  cafeName: "",
};

export default CafeTransactionReportModal;
